import { ReactNode } from 'react';
import { PrimaryButton, Spinner } from '@vertex-protocol/web-ui';
import { ButtonStateContent } from 'client/components/ButtonStateContent';
import { NadoWithdrawButtonState } from 'client/modules/collateral/nadoWithdraw/types';

interface Props {
  state: NadoWithdrawButtonState;
  chainName?: string;
  inputError?: string;
  bridgeProvider?: 'cctp' | 'oft' | 'across' | 'none';
  tokenSymbol?: string;
  isFundingAutomationAvailable?: boolean;
  isFundingAutomationActive?: boolean;
  onClick?: () => void;
}

/**
 * Multi-state submit button for the Nado withdrawal flow.
 * Shows 3-step progress labels: 1/3 Withdraw → 2/3 Approve → 3/3 Bridge
 */
export function NadoWithdrawSubmitButton({
  state,
  chainName = 'Chain',
  inputError,
  bridgeProvider = 'across',
  tokenSymbol = 'USDT0',
  isFundingAutomationAvailable = false,
  isFundingAutomationActive = false,
  onClick,
}: Props) {
  // Error state overrides normal button
  if (inputError === 'below_fees') {
    return (
      <PrimaryButton
        disabled
        type="submit"
        className="w-full bg-negative/20 text-negative hover:bg-negative/20"
      >
        Try a higher amount
      </PrimaryButton>
    );
  }

  const { label, isDisabled } = isFundingAutomationAvailable
    ? getFundingAutomationConfig(state, isFundingAutomationActive)
    : getButtonConfig(state, chainName, bridgeProvider, tokenSymbol);

  return (
    <PrimaryButton
      disabled={isDisabled}
      onClick={onClick}
      type="submit"
      className="w-full"
    >
      {label}
    </PrimaryButton>
  );
}

function getFundingAutomationConfig(
  state: NadoWithdrawButtonState,
  isActive: boolean,
): ButtonConfig {
  switch (state) {
    case 'idle':
      return { label: 'Enter Amount', isDisabled: true };

    case 'route_unavailable':
      return { label: 'Bridge Route Unavailable', isDisabled: true };

    case 'wallet_mismatch':
      return { label: 'Wallet Changed', isDisabled: true };

    case 'partial_arrival':
      return { label: 'Refresh Arrival', isDisabled: false };

    case 'arrival_timeout':
      return { label: 'Retry Arrival Check', isDisabled: false };

    case 'success':
      return {
        label: <ButtonStateContent.Success message="Withdrawal Complete" />,
        isDisabled: false,
      };

    case 'switching_chain':
    case 'preparing_bridge':
    case 'withdrawing':
    case 'approving_oft':
    case 'bridging':
    case 'bridge_pending':
      return {
        label: <SpinnerLabel text="Completing Withdrawal..." />,
        isDisabled: true,
      };

    case 'requires_switch_chain':
    case 'ready_withdraw':
    case 'requires_approve_oft':
    case 'ready_bridge':
      return {
        label: isActive ? <SpinnerLabel text="Completing Withdrawal..." /> : 'Withdraw',
        isDisabled: isActive,
      };
  }
}

function SpinnerLabel({ text }: { text: string }) {
  return (
    <span className="flex items-center justify-center gap-x-2">
      <Spinner size={16} className="fill-current" />
      <span>{text}</span>
    </span>
  );
}

interface ButtonConfig {
  label: ReactNode;
  isDisabled: boolean;
}

function getButtonConfig(
  state: NadoWithdrawButtonState,
  chainName: string,
  bridgeProvider: NonNullable<Props['bridgeProvider']>,
  tokenSymbol: string,
): ButtonConfig {
  const isAcross = bridgeProvider === 'across';
  const isNoBridge = bridgeProvider === 'none';
  const total = isNoBridge ? 1 : isAcross ? 2 : 3;
  const bridgeStep = isAcross ? 2 : 3;

  switch (state) {
    case 'idle':
      return { label: 'Enter Amount', isDisabled: true };

    case 'requires_switch_chain':
      return { label: 'Switch to Ink', isDisabled: false };

    case 'switching_chain':
      return { label: <SpinnerLabel text="Switching..." />, isDisabled: true };

    case 'route_unavailable':
      return { label: 'Bridge Route Unavailable', isDisabled: true };

    case 'wallet_mismatch':
      return { label: 'Wallet Changed', isDisabled: true };

    case 'partial_arrival':
      return { label: 'Refresh Arrival', isDisabled: false };

    case 'arrival_timeout':
      return { label: 'Retry Arrival Check', isDisabled: false };

    case 'ready_withdraw':
      return {
        label: isNoBridge ? `Withdraw ${tokenSymbol}` : `1/${total}: Start Withdrawal`,
        isDisabled: false,
      };

    case 'withdrawing':
      return {
        label: (
          <SpinnerLabel
            text={isNoBridge ? `Withdrawing ${tokenSymbol}...` : `1/${total}: Withdrawing ${tokenSymbol}...`}
          />
        ),
        isDisabled: true,
      };

    case 'requires_approve_oft':
      return { label: '2/3: Approve Bridge Tx', isDisabled: false };

    case 'approving_oft':
      return { label: <SpinnerLabel text="2/3: Approving..." />, isDisabled: true };

    case 'ready_bridge':
      return {
        label: `${bridgeStep}/${total}: Bridge to ${chainName}`,
        isDisabled: false,
      };

    case 'preparing_bridge':
      return {
        label: <SpinnerLabel text={`${bridgeStep}/${total}: Preparing bridge...`} />,
        isDisabled: true,
      };

    case 'bridging':
      return {
        label: <SpinnerLabel text={`${bridgeStep}/${total}: Bridging...`} />,
        isDisabled: true,
      };

    case 'bridge_pending':
      return {
        label: <SpinnerLabel text={`${bridgeStep}/${total}: Waiting for bridge fill...`} />,
        isDisabled: true,
      };

    case 'success':
      return {
        label: <ButtonStateContent.Success message="Withdrawal Complete" />,
        isDisabled: false,
      };
  }
}
